import React, { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] dark:focus-visible:ring-accent-light dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-accent text-accent-foreground hover:bg-accent-hover hover:-translate-y-[1px] shadow-[0_4px_10px_rgba(0,168,232,0.2)] hover:shadow-[0_6px_15px_rgba(0,168,232,0.35)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:-translate-y-[1px] shadow-[0_2px_5px_rgba(239,68,68,0.3)]",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, "aria-label": ariaLabel, ...props }, ref) => {
    if (process.env.NODE_ENV !== "production" && asChild && React.Children.count(children) !== 1) {
      // biome-ignore lint/suspicious/noConsole: development-time component warning
      console.warn("Button: asChild requires exactly one child element");
    }
    if (process.env.NODE_ENV !== "production" && !asChild && !children && !ariaLabel) {
      // biome-ignore lint/suspicious/noConsole: development-time a11y warning
      console.warn("Button: icon-only buttons must provide an aria-label");
    }
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} aria-label={ariaLabel} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} aria-label={ariaLabel} {...props}>
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
